import ResourcesDispatcher from "../config/ResourcesDispatcher";
import Utils from "../utils/Utils";
export default class GameAnimations {
    static testMethod() {
        console.log("✅=^.^= GameAnimations library is connected successfully!");
    }
    //Il take event del gioco posso gestirlo tramite get e set
    static resetTakeEvent(getTakeEvent, setTakeEvent) {
        const currentEvent = getTakeEvent();
        if (currentEvent) {
            currentEvent.remove(false);
        }
        setTakeEvent(undefined);
    }
    /**
     * Tweens a numeric counter from `from` to `to`, updating `text` on every
     * frame via `Utils.formatValue`. Returns the created Tween so the
     * caller can stop/complete it at any time, and resolves the optional
     * Promise when the tween completes.
     *
     * @param scene    - The active Phaser scene (provides the tween manager)
     * @param text     - The Phaser Text object to update
     * @param from     - Starting value
     * @param to       - Target value
     * @param duration - Duration in milliseconds (default 1000)
     * @param onDone   - Optional callback invoked when the tween completes
     */
    static tweenCounter(text, from, to, duration = 1000, onDone) {
        const tweenTarget = { value: from };
        return scene.tweens.add({
            targets: tweenTarget,
            value: { from, to },
            duration,
            ease: "Linear",
            onUpdate: () => {
                text.setText(Utils.formatValue(tweenTarget.value));
            },
            onComplete: () => {
                text.setText(Utils.formatValue(to));
                onDone?.();
            }
        });
    }
    static playWinningTween(tweenedValues, win, endScale, duration, winAmountText, soundToPlay, changeStateMachineCallback, getTakeEvent, setTakeEvent, onUpdateCallback) {
        return scene.tweens.add({
            targets: tweenedValues,
            value: win,
            scale: endScale,
            duration,
            ease: "Power1",
            onStart: () => {
                this.resetTakeEvent(getTakeEvent, setTakeEvent);
                winAmountText.setVisible(true);
                if (!gameLogicVariableManager.getTurbo()) {
                    soundManager.playCoinsSound();
                }
                else {
                    soundManager.play(soundToPlay);
                }
                changeStateMachineCallback();
            },
            onUpdate: () => {
                onUpdateCallback(tweenedValues);
            },
            onComplete: () => {
                animationManager.pointsToTweenOnCompleteFunction();
            }
        });
    }
    static async playWinCombinationAnimation(animatedCells, unanimatedCells, lineIndex, moreThanOneLine, startWinningCombinationsTextTween, stopWinningCombinationsTextTween) {
        // Mostra e anima le celle
        animatedCells.forEach((cell) => {
            cell.turnOn();
            cell.showFrame();
        });
        unanimatedCells.forEach((cell) => {
            cell.turnOff();
            cell.showFrame();
        });
        startWinningCombinationsTextTween(!moreThanOneLine);
        if (moreThanOneLine) {
            await Promise.all(animatedCells.map((cell) => cell.playAnimation(false)));
            stopWinningCombinationsTextTween();
            animatedCells.forEach((cell) => {
                cell.turnOff();
                cell.hideFrame();
            });
            unanimatedCells.forEach((cell) => {
                cell.turnOff();
                cell.hideFrame();
            });
        }
        else {
            animatedCells.forEach((cell) => {
                cell.playAnimation(true);
            });
        }
    }
    // funzione che si occupa di ciclare le animazioni di vincita di qualunque combinazione
    static processWinningCellAnimation(reelIndex, cellIndex, animatedCells, unanimatedCells, cell, expanding) {
        if (cellIndex > 0) {
            const wildInfo = gameDataManager.getWildInfo()?.filter((wildInfo) => wildInfo.reelIndex === reelIndex);
            // caso in cui c'è l'extra wild che si espande
            if (wildInfo?.length > 0 && expanding) {
                animatedCells.push(expanding);
            }
            // caso in cui la cella extra wild si moltiplica
            else {
                animatedCells.push(cell);
            }
        }
        else {
            unanimatedCells.push(cell);
        }
    }
    static async animateAllSymbolsWithMemo(reels, memo) {
        const animatedCells = [];
        reels.forEach((reel) => {
            reel.cells.forEach((cell) => {
                if (cell.getMemo() === memo) {
                    animatedCells.push(cell);
                }
            });
        });
        await Promise.all(animatedCells.map((cell) => cell.playAnimation(false)));
    }
    static async animateAllSymbolsWithMemoList(reels, memos) {
        const animatedCells = [];
        reels.forEach((reel) => {
            reel.cells.forEach((cell) => {
                if (memos.includes(cell.getMemo())) {
                    animatedCells.push(cell);
                }
            });
        });
        await Promise.all(animatedCells.map((cell) => cell.playAnimation(false)));
    }
    static async animateWilds(reels, expanding) {
        const wildInfoList = gameDataManager.getWildInfo();
        const winningCombinations = gameDataManager.getWinningCombinations();
        if (!wildInfoList.length)
            return;
        const animations = wildInfoList.map((wildInfo) => {
            const { reelIndex, cellIndex } = wildInfo;
            const reelIsWinning = winningCombinations?.length > 0 && winningCombinations.some((combo) => combo.positions[reelIndex] > 0);
            // se è un expanding wild usa la gestione del wild che si espande, altrimenti usa la gestione delle celle animate con extra wild
            return expanding
                ? this.extraWildExpanding(reels, reelIndex, cellIndex, reelIsWinning)
                : this.extraWildInCells(reels, reelIndex, cellIndex, reelIsWinning);
        });
        await Promise.all(animations);
    }
    static extraWildExpanding(reels, reelIndex, cellIndex, reelIsWinning) {
        const expandingWild = reels[reelIndex].expandingWild;
        expandingWild.setIndex(cellIndex);
        expandingWild.expandWild(reelIsWinning);
    }
    static extraWildInCells(reels, reelIndex, cellIndex, reelIsWinning) {
        return new Promise((resolve) => {
            const cellsWithExtraWild = reels[reelIndex].getCells();
            const numCells = ResourcesDispatcher.GAME_CONSTANTS.NUM_OF_CELLS_PER_REEL;
            const S_EXTRA_WILD = ResourcesDispatcher.GAME_CONSTANTS.S_EXTRA_WILD;
            const wildImageInfo = ResourcesDispatcher.GAME_CONSTANTS.symbolImagesOn?.[S_EXTRA_WILD];
            // If the source cell is outside the visible range or we lack image info, fall back to instant show
            const sourceIsValid = cellIndex > 0 && cellIndex <= numCells && wildImageInfo;
            if (!sourceIsValid) {
                cellsWithExtraWild.forEach((ew, index) => {
                    if (index > 0 && index <= numCells) {
                        ew.setCellInfo(S_EXTRA_WILD);
                        ew.extraWildAnimation(reelIsWinning);
                    }
                });
                resolve();
                return;
            }
            // Show the wild immediately on the extraction cell
            const sourceCell = cellsWithExtraWild[cellIndex];
            sourceCell.setCellInfo(S_EXTRA_WILD);
            sourceCell.extraWildAnimation(reelIsWinning);
            // World position of the source image (used as starting point for the flying clones)
            const sourceWorldMatrix = sourceCell.getImage().getWorldTransformMatrix();
            const sourceX = sourceWorldMatrix.tx;
            const sourceY = sourceWorldMatrix.ty;
            const worldScale = sourceWorldMatrix.scaleX;
            // Collect promises for every flying clone so we can await them all
            const flyPromises = [];
            cellsWithExtraWild.forEach((ew, index) => {
                if (index <= 0 || index > numCells || index === cellIndex)
                    return;
                const targetWorldMatrix = ew.getImage().getWorldTransformMatrix();
                const targetX = targetWorldMatrix.tx;
                const targetY = targetWorldMatrix.ty;
                const distance = Math.abs(index - cellIndex);
                const flyingWild = scene.add.image(sourceX, sourceY, wildImageInfo.key, wildImageInfo.frame)
                    .setDepth(1000)
                    .setScale(worldScale);
                const flyPromise = new Promise((resolveFly) => {
                    scene.tweens.add({
                        targets: flyingWild,
                        x: targetX,
                        y: targetY,
                        duration: 450,
                        ease: 'Power2',
                        delay: (distance - 1) * 80,
                        onComplete: () => {
                            flyingWild.destroy();
                            ew.setCellInfo(S_EXTRA_WILD);
                            ew.extraWildAnimation(reelIsWinning);
                            resolveFly();
                        }
                    });
                });
                flyPromises.push(flyPromise);
            });
            Promise.all(flyPromises).then(() => resolve());
        });
    }
    static async collapseWilds(reels) {
        const wildInfoList = gameDataManager.getWildInfo();
        const flatGrid = gameDataManager.getGrid();
        if (!wildInfoList.length)
            return;
        const animations = wildInfoList.map((wildInfo) => {
            const { reelIndex, cellIndex } = wildInfo;
            const numCells = ResourcesDispatcher.GAME_CONSTANTS.NUM_OF_CELLS_PER_REEL;
            // getGrid() returns a flat array (all reels concatenated); slice out the numCells
            // values belonging to this reel. Cell indices in the reel are 1-based (index 0 is
            // a hidden buffer), so reelMemos[index - 1] maps to the correct flat-array entry.
            const reelMemos = flatGrid.slice(reelIndex * numCells, (reelIndex + 1) * numCells);
            return new Promise((resolve) => {
                const cellsWithExtraWild = reels[reelIndex].getCells();
                const S_EXTRA_WILD = ResourcesDispatcher.GAME_CONSTANTS.S_EXTRA_WILD;
                const wildImageInfo = ResourcesDispatcher.GAME_CONSTANTS.symbolImagesOn?.[S_EXTRA_WILD];
                const sourceIsValid = cellIndex > 0 && cellIndex <= numCells && wildImageInfo;
                if (!sourceIsValid) {
                    cellsWithExtraWild.forEach((ew, index) => {
                        if (index > 0 && index <= numCells) {
                            ew.setCellInfo(reelMemos[index - 1]);
                        }
                    });
                    resolve();
                    return;
                }
                const sourceCell = cellsWithExtraWild[cellIndex];
                const sourceWorldMatrix = sourceCell.getImage().getWorldTransformMatrix();
                const sourceX = sourceWorldMatrix.tx;
                const sourceY = sourceWorldMatrix.ty;
                const worldScale = sourceWorldMatrix.scaleX;
                const flyPromises = [];
                cellsWithExtraWild.forEach((ew, index) => {
                    if (index <= 0 || index > numCells || index === cellIndex)
                        return;
                    const startWorldMatrix = ew.getImage().getWorldTransformMatrix();
                    const startX = startWorldMatrix.tx;
                    const startY = startWorldMatrix.ty;
                    const distance = Math.abs(index - cellIndex);
                    const flyingWild = scene.add.image(startX, startY, wildImageInfo.key, wildImageInfo.frame)
                        .setDepth(1000)
                        .setScale(worldScale);
                    // Restore original symbol immediately as the clone departs
                    ew.setCellInfo(reelMemos[index - 1]);
                    const flyPromise = new Promise((resolveFly) => {
                        scene.tweens.add({
                            targets: flyingWild,
                            x: sourceX,
                            y: sourceY,
                            duration: 450,
                            ease: 'Power2',
                            delay: (distance - 1) * 80,
                            onComplete: () => {
                                flyingWild.destroy();
                                resolveFly();
                            }
                        });
                    });
                    flyPromises.push(flyPromise);
                });
                Promise.all(flyPromises).then(() => {
                    sourceCell.setCellInfo(reelMemos[cellIndex - 1]);
                    resolve();
                });
            });
        });
        await Promise.all(animations);
    }
    static async contractWilds(gameArea) {
        let expandingWilds = [];
        gameDataManager.getWildInfo().forEach((wildInfo) => {
            const expandingWild = gameArea.reels[wildInfo.reelIndex].expandingWild;
            expandingWild.setIndex(wildInfo.cellIndex);
            expandingWilds.push(expandingWild);
        });
        await Promise.all(expandingWilds.map((wild) => wild.contractWild()));
    }
    static playFreeSpinWinningAnimation(reels, numCell) {
        const animatedCells = [];
        reels.forEach((reel) => {
            for (let i = 1; i < numCell; i++) {
                animatedCells.push(reel.freeSpinCells[i]);
            }
        });
        animatedCells.forEach((cell) => cell.playAnimation(true));
    }
    static setWinningLines(combinations) {
        let winningLines = [];
        for (let i = 0; i < combinations.length; i++) {
            winningLines.push({
                symbolPositions: combinations[i].positions,
                index: combinations[i].numOfLine,
                repetitions: combinations[i].repeat,
                symbolIndex: combinations[i].symbol,
                win: combinations[i].win,
                animated: false,
                scatterLine: false
            });
        }
        return winningLines;
    }
    static stopWinCombinationAnimated(animatedCells) {
        animatedCells?.forEach((cell) => {
            cell.stopAnimation();
            cell.hideFrame();
            cell.turnOff();
        });
    }
    static stopWinCombinationUnanimated(unanimatedCells) {
        unanimatedCells?.forEach((cell) => {
            cell.hideFrame();
            cell.turnOff();
        });
    }
    static playEntryToBonusSceneSpineAnimation(type) {
        return new Promise((resolve) => {
            let skipText = document.createElement('div');
            skipText.textContent = 'CLICK OR TAP TO SKIP';
            skipText.style.cssText = [
                'position:fixed',
                'bottom:20px',
                'left:50%',
                'transform:translateX(-50%)',
                'color:white',
                'font-size:16px',
                'font-family:sans-serif',
                'letter-spacing:2px',
                'z-index:9999',
                'display:none',
                'pointer-events:none',
                'white-space:nowrap',
                'text-shadow:0 0 6px #000,0 0 6px #000'
            ].join(';');
            document.body.appendChild(skipText);
            let spineName;
            const orientation = generalManager.getOrientation();
            switch (type) {
                case "freespin":
                    soundManager.play(ResourcesDispatcher.SOUNDS.ENTER_BONUS);
                    scene.setBackgroundImage(ResourcesDispatcher.GAME_CONSTANTS.GAME_MODE.FREEGAME); //per il freespin
                    window.isFreeSpinEntryPlaying = true;
                    spineName = orientation === "portrait" ? ResourcesDispatcher.SPINES.FreeSpin_Entry_pt : ResourcesDispatcher.SPINES.FreeSpin_Entry;
                    break;
                case "pickbonus":
                    soundManager.play(ResourcesDispatcher.SOUNDS.ENTER_BONUS);
                    scene.setBackgroundImage(ResourcesDispatcher.GAME_CONSTANTS.GAME_MODE.PICKBONUS);
                    window.isPickBonusPlaying = true;
                    spineName = orientation === "portrait" ? ResourcesDispatcher.SPINES.PickBonus_Entry_pt : ResourcesDispatcher.SPINES.PickBonus_Entry;
                    break;
                case "linkspin":
                    window.isLinkSpinEntryPlaying = true;
                    break;
                default:
                    break;
            }
            let spine = scene.add.spine(scene.scale.width / 2, scene.scale.height / 2, spineName.jsonKey, spineName.atlasKey);
            spine.animationState.setAnimation(0, "animation", true);
            skipText.style.display = 'block';
            if (generalManager.getOrientation() === "portrait") {
                skipText.style.fontSize = '15px';
            }
            const finish = () => {
                scene.input.off("pointerdown", onSkip);
                scene.input.keyboard?.off("keydown-SPACE", onSkip);
                spine.animationState.removeListener(completeListener);
                spine.destroy();
                skipText.style.display = 'none';
                if (type === "freespin")
                    window.isFreeSpinEntryPlaying = false;
                else if (type === "pickbonus")
                    window.isPickBonusPlaying = false;
                else
                    window.dispatchEvent(new Event("resize"));
                resolve();
            };
            const onSkip = () => finish();
            let loopCount = 0;
            const completeListener = {
                complete: () => {
                    loopCount++;
                    if (loopCount < 1)
                        return;
                    finish();
                }
            };
            spine.animationState.addListener(completeListener);
            scene.input.once("pointerdown", onSkip);
            scene.input.keyboard?.once("keydown-SPACE", onSkip);
        });
    }
}
